export const DEPLOYMENT_ENVIRONMENTS = ['staging', 'production'] as const;
export type DeploymentEnvironment = (typeof DEPLOYMENT_ENVIRONMENTS)[number];

export const DEPLOYMENT_MODES = ['runtime', 'infrastructure', 'rollback'] as const;
export type DeploymentMode = (typeof DEPLOYMENT_MODES)[number];

export const DEPLOYMENT_STATUSES = ['in_progress', 'success', 'failure', 'cancelled', 'skipped'] as const;
export type DeploymentStatus = (typeof DEPLOYMENT_STATUSES)[number];

export const DEPLOYMENT_PHASES = ['build', 'deploy'] as const;
export type DeploymentPhase = (typeof DEPLOYMENT_PHASES)[number];

export const DEPLOYMENT_STATS_DAY_OPTIONS = [7, 30, 90] as const;
export type DeploymentStatsDays = (typeof DEPLOYMENT_STATS_DAY_OPTIONS)[number];

export const DEPLOYMENT_HISTORY_CURRENT_SK = 'CURRENT';

/** Workflow groups Dev Console can disable/enable: one per deploy environment, plus OTA updates. */
export const DEPLOYMENT_PIPELINES = [...DEPLOYMENT_ENVIRONMENTS, 'ota'] as const;
export type DeploymentPipeline = (typeof DEPLOYMENT_PIPELINES)[number];

/**
 * Workflows toggled by Dev Console Disable/Enable pipelines.
 * Rollback (`rollback-deploy.yml`) is intentionally excluded so recovery stays available.
 */
export const DEPLOYMENT_PIPELINE_WORKFLOWS = {
  staging: ['deploy-01-staging.yml'],
  production: ['deploy-02-production.yml'],
  // `eas-update.yml` publishes every environment's OTAs, so this group is not per environment.
  // The emergency iOS wrapper calls it via workflow_call and is disabled alongside it.
  ota: ['eas-update.yml', 'eas-update-production-ios-to-staging.yml'],
} as const satisfies Record<DeploymentPipeline, readonly string[]>;

/** What one Disable/Enable request toggles: a single pipeline, or `all` of them at once for incidents. */
export const DEPLOYMENT_PIPELINE_TARGETS = [...DEPLOYMENT_PIPELINES, 'all'] as const;
export type DeploymentPipelineTarget = (typeof DEPLOYMENT_PIPELINE_TARGETS)[number];

export function deploymentPipelineTargetWorkflows(target: DeploymentPipelineTarget): readonly string[] {
  if (target === 'all') return DEPLOYMENT_PIPELINES.flatMap((pipeline) => DEPLOYMENT_PIPELINE_WORKFLOWS[pipeline]);
  return DEPLOYMENT_PIPELINE_WORKFLOWS[target];
}

export type DeploymentPipelineAction = 'disable' | 'enable';

export const MERGE_FREEZE_REASON_MAX_LENGTH = 1000;

export const DEV_CONSOLE_PUBLIC_ORIGIN = 'https://dev.nourish.com';

export function deploymentHistoryPk(environment: DeploymentEnvironment, mode: DeploymentMode): string {
  return `${environment}#${mode}`;
}

export function deploymentHistorySk(startedAt: string, workflowRunId: string): string {
  return `${startedAt}#${workflowRunId}`;
}

export function deploymentConsoleUrl(environment: DeploymentEnvironment, workflowRunId?: string): string {
  const params = new URLSearchParams({ environment });
  if (workflowRunId != null && workflowRunId !== '') {
    params.set('run', workflowRunId);
  }
  return `${DEV_CONSOLE_PUBLIC_ORIGIN}/deployments?${params.toString()}`;
}
